<template>
  <div class="user-account-menu-wrapper">
    <v-menu
      v-model="menu"
      top
      :offset-x="true"
      :nudge-top="10"
      :nudge-left="0"
      :close-on-content-click="false"
      max-width="360px"
      min-width="300px"
      content-class="user-account-menu-content"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-list-item
          link
          v-bind="attrs"
          v-on="on"
          :input-value="menu"
          class="user-account-drawer-item"
        >
          <v-list-item-icon>
            <v-icon>mdi-account-circle-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ AuthLogic.user().first_name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <v-card class="user-account-card">
        <v-list dense>
          <v-list-item class="pb-1">
            <v-list-item-avatar color="primary" size="40">
              <v-icon dark>mdi-account</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">
                {{ user.first_name }} {{ user.last_name }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption grey--text">
                {{ user.email }}
              </v-list-item-subtitle>
              <v-list-item-subtitle style="color:#489FBB" class="caption">
                Rôle : <span v-for="(role, roleIndex) in user.roles" :key="role.name || roleIndex">{{ role.value }} {{ user.roles.length > 1 && roleIndex < user.roles.length - 1 ? ' / ' : '' }}</span>
              </v-list-item-subtitle>
              <div class="mt-1" v-if="user && user.unitGroups && user.unitGroups.length === 1">
                <div class="caption" style="color:#489FBB">Groupe : {{ user.unitGroups[0].name }}</div>
              </div>
            </v-list-item-content>
          </v-list-item>

          <template v-if="!savingPreferred">
            <v-divider class="my-1"></v-divider>
          </template>
          <v-progress-linear
            v-else
            class="my-1"
            color="primary"
            height="2"
            indeterminate
          ></v-progress-linear>

          <!-- Mon organisation: show when multiple groups and has permission -->
          <v-list-group
            v-if="AuthLogic.hasPermission('account.organization') && user && user.unitGroups && user.unitGroups.length > 1"
            prepend-icon="mdi-office-building"
            no-action
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title>Mon organisation</v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item>
              <v-list-item-content class="pt-0">
                <div class="caption grey--text text--darken-1 mb-2">
                  {{ groupsCount }} groupes disponibles. Cliquez pour définir votre favori :
                </div>
                <div class="d-flex flex-wrap">
                  <div v-for="ug in displayedUnitGroups" :key="ug.id">
                    <v-chip
                      class="ma-1"
                      small
                      outlined
                      v-if="user.preferredUnitGroup && user.preferredUnitGroup.id === ug.id"
                      color="primary"
                    >
                      {{ ug.name }}
                      <v-icon x-small class="ml-1" color="amber">mdi-star</v-icon>
                    </v-chip>

                    <v-tooltip v-else top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          class="ma-1"
                          small
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="setPreferredUnitGroup(ug.id)"
                        >
                          {{ ug.name }}
                          <v-icon x-small class="ml-1">mdi-star-outline</v-icon>
                        </v-chip>
                      </template>
                      <span>{{ $t('components.account.set_favorite') }}</span>
                    </v-tooltip>
                  </div>
                </div>
                <div v-if="hasMoreThanFive" class="mt-1">
                  <v-btn x-small text color="primary" @click="showAllGroups = !showAllGroups">
                    {{ showAllGroups ? 'voir moins' : 'voir plus' }}
                  </v-btn>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>

          <!-- Rating display settings (Mes indicateurs) -->
          <v-list-item
            v-if="AuthLogic.hasPermission('account.indicators')"
            @click="openDialog('ratingDisplaySettingsDialog')"
          >
            <v-list-item-icon>
              <v-icon>mdi-star-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('components.account.indicators') }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon small color="grey">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <!-- Preferences -->
          <v-list-item
            v-if="AuthLogic.hasPermission('account.preferences')"
            @click="openDialog('preferencesDialog')"
          >
            <v-list-item-icon>
              <v-icon>mdi-cog-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('components.preferences.menu') }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon small color="grey">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <!-- Mes permissions -->
          <v-list-item @click="openDialog('userPermissionsPanel')">
            <v-list-item-icon>
              <v-icon>mdi-shield-account-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Mes permissions</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon small color="grey">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <!-- Export -->
          <v-list-item
            v-if="AuthLogic.hasPermission('account.exports')"
            @click="openDialog('exportDialog')"
          >
            <v-list-item-icon>
              <v-icon>mdi-file-export-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('components.export_dialog.menu') }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon small color="grey">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <v-divider class="my-1"></v-divider>

          <!-- Logout -->
          <v-list-item @click="logout">
            <v-list-item-icon>
              <v-icon color="error">mdi-power</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="red--text text--darken-1">{{ $t('views.layout.logout') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Dialogs -->
    <rating-display-settings-dialog ref="ratingDisplaySettingsDialog"></rating-display-settings-dialog>
    <preferences-dialog ref="preferencesDialog"></preferences-dialog>
    <user-permissions-panel ref="userPermissionsPanel" />
    <export-dialog ref="exportDialog"></export-dialog>
  </div>
</template>

<script>
import AuthLogic from '@/logics/AuthLogic'
import ExportDialog from '@/components/communs/ExportDialog'
import RatingDisplaySettingsDialog from '@/components/communs/RatingDisplaySettingsDialog'
import PreferencesDialog from '@/components/communs/PreferencesDialog'
import UserPermissionsPanel from '@/components/user/UserPermissionsPanel'
import ModelLogic from '@/logics/ModelLogic'
import { EventBus } from '@/services/Request'
import _ from 'lodash'

export default {
  name: 'UserAccountMenu',
  components: {
    ExportDialog,
    RatingDisplaySettingsDialog,
    PreferencesDialog,
    UserPermissionsPanel
  },
  data: () => ({
    AuthLogic,
    user: AuthLogic.user(),
    menu: false,
    savingPreferred: false,
    showAllGroups: false,
  }),
  created() {
    this.setUserRoles()
  },
  computed: {
    rolesValues() {
      let roles = [
        {
          id: '8a0842d7-33b9-476b-9538-76ed633edb62',
          name: 'reader',
          value: this.$t('views.user.roles.reader')
        },
        {
          id: 'c9619786-9779-43dd-a79b-e919d9fae47d',
          name: 'analyste',
          value: this.$t('views.user.roles.analyste')
        },
        {
          id: 'c8619786-9789-43cd-rg89-e919d9fae47d',
          name: 'analyste_expert',
          value: this.$t('views.user.roles.analyste_expert')
        },
        {
          id: '08902735-dfde-46a8-9fdf-645a48b02b3c',
          name: 'relation_client',
          value: this.$t('views.user.roles.relation_client')
        }
      ]

      if (AuthLogic.hasPermission('users.manage')) {
        roles.unshift({
          id: 'bed9d4f1-c648-4435-b9e3-8dd09593381b',
          name: 'admin',
          value: this.$t('views.user.roles.admin')
        })
      }
      return roles
    },
    groupsCount() {
      return this.user && Array.isArray(this.user.unitGroups) ? this.user.unitGroups.length : 0
    },
    hasMoreThanFive() {
      return this.groupsCount > 5
    },
    displayedUnitGroups() {
      const list = this.user && Array.isArray(this.user.unitGroups) ? this.user.unitGroups : []
      return this.showAllGroups ? list : list.slice(0, 5)
    }
  },
  methods: {
    openDialog(refName) {
      this.menu = false
      if (this.$refs[refName] && typeof this.$refs[refName].openDialog === 'function') {
        this.$refs[refName].openDialog()
      }
    },
    async setPreferredUnitGroup(unitGroupId) {
      try {
        const currentPreferredId =
          this.user.preferredUnitGroup && this.user.preferredUnitGroup.id
            ? this.user.preferredUnitGroup.id
            : null
        if (this.savingPreferred || currentPreferredId === unitGroupId) return
        this.savingPreferred = true
        await ModelLogic.updateOne('users', this.user.id, {
          preferredUnitGroup: { id: unitGroupId },
          with: ['unitGroups', 'metas', 'ratingSources', 'preferredUnitGroup']
        })
        const result = await AuthLogic.loadUser()
        this.user = result.data
        this.setUserRoles()
        EventBus.$emit('user-account-updated', { type: 'preferred-unit-group' })
        EventBus.$emit('show-snackbar', { text: 'Organisation favorite mise à jour', timeout: 2500 })
      } catch (e) {
        console.error('Failed to set preferred unit group', e)
      } finally {
        this.savingPreferred = false
      }
    },
    async logout() {
      this.menu = false
      let logoutResult = await AuthLogic.logout()
      if (logoutResult.redirect) {
        window.location.href = logoutResult.redirect
        return
      }
      await this.$router.push({ name: 'home' })
    },
    setUserRoles() {
      const original = this.user && Array.isArray(this.user.roles) ? this.user.roles : []
      const roleNames = original
        .map(r => (typeof r === 'string' ? r : r && r.name ? r.name : null))
        .filter(n => !!n)
      const rolesByName = _.keyBy(this.rolesValues, 'name')
      this.user.roles = roleNames.map(n => {
        const found = rolesByName[n]
        return found ? { id: found.id, name: found.name, value: found.value } : { name: n, value: n }
      })
    }
  }
}
</script>

<style scoped>
.user-account-menu-wrapper {
  width: 100%;
}
.user-account-card {
  border-radius: 8px;
  overflow: hidden;
}
</style>
